import { useContext, useMemo, useState } from "react";
import { Table, Button, Modal, Form, Input, Select, InputNumber, Switch, message } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useUsers, useCreateUser, useUpdateUser, useDeleteUser, CreateUserPayload } from "../../queries/users";
import { useClients } from "../../queries/clients";
import { useProjects } from "../../queries/projects";
import { SessionUser, ClientProps, ProjectProps } from "../../types/interfaces";
import { ApiError } from "../../hooks/apiRequest";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell, Toolbar } from "../../components/ui/PageShell";
import { StatusTag, RoleTag } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";
import { NoAccess } from "../../components/ui/NoAccess";
import AuthContext from "../../context/AuthContext";

const ROLES: SessionUser["role"][] = ["admin", "user", "final_user"];
const ROLE_LABEL: Record<SessionUser["role"], string> = {
  admin: "Admin", user: "Employee", final_user: "Client contact",
};

export const UserListPage = () => {
  const { userData } = useContext(AuthContext);
  const isAdmin = userData?.role === "admin";

  const { data: users, isLoading, isError, error } = useUsers({ enabled: isAdmin });
  const { data: clients } = useClients({ enabled: isAdmin });
  const { data: projects } = useProjects({ enabled: isAdmin });
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<SessionUser | null>(null);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<SessionUser["role"] | undefined>();
  const [form] = Form.useForm();

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (users || [])
      .filter((u: SessionUser) => (roleFilter ? u.role === roleFilter : true))
      .filter((u: SessionUser) => (q ? (u.name + " " + u.username).toLowerCase().includes(q) : true));
  }, [users, query, roleFilter]);

  const openCreateModal = () => { setEditingUser(null); form.resetFields(); setIsModalOpen(true); };
  const openEditModal = (user: SessionUser) => {
    form.resetFields(); setEditingUser(user); form.setFieldsValue(user); setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    try {
      if (editingUser) await updateUser.mutateAsync({ id: editingUser._id, data: values });
      else await createUser.mutateAsync(values as CreateUserPayload);
      setIsModalOpen(false);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not save the user");
    }
  };

  const handleDelete = (userId: string) => {
    Modal.confirm({
      title: "Delete this user?",
      content: "They lose access immediately. Their logged time stays in reports.",
      okText: "Delete user",
      okButtonProps: { danger: true },
      onOk: async () => {
        try { await deleteUser.mutateAsync(userId); }
        catch (err) { message.error(err instanceof ApiError ? err.message : "Could not delete the user"); }
      },
    });
  };

  if (!isAdmin) return <NoAccess what="user administration" />;

  const activeCount = (users || []).filter((u: SessionUser) => u.active).length;

  return (
    <PageShell
      kind="list"
      kicker="Administration"
      title="Users"
      meta={[
        { label: "Total", value: <span className="q-num">{(users || []).length}</span> },
        { label: "Active", value: <span className="q-num">{activeCount}</span> },
      ]}
      actions={<Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>New user</Button>}
    >
      <Toolbar count={rows.length} unit="users">
        <Input
          allowClear
          prefix={<SearchOutlined style={{ color: "var(--q-text-faint)" }} />}
          placeholder="Search name or username"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 240 }}
        />
        <Select
          allowClear
          style={{ width: 180 }}
          placeholder="All roles"
          value={roleFilter}
          onChange={setRoleFilter}
          options={ROLES.map((r) => ({ label: ROLE_LABEL[r], value: r }))}
        />
      </Toolbar>

      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
        <Table<SessionUser>
          rowKey="_id"
          size="small"
          dataSource={rows}
          pagination={rows.length > 25 ? { pageSize: 25, size: "small" } : false}
          locale={{ emptyText: <EmptyState title="No users match" description="Clear the filters to see everyone." /> }}
          columns={[
            {
              title: "Name", dataIndex: "name",
              render: (name: string, r) => (
                <span>
                  <span style={{ fontWeight: 500 }}>{name}</span>
                  {r._id === userData?._id && <span style={{ color: "var(--q-text-faint)", marginLeft: 6 }}>you</span>}
                </span>
              ),
            },
            {
              title: "Username", dataIndex: "username", width: 180, responsive: ["lg"],
              render: (v) => <span style={{ color: "var(--q-text-muted)" }}>{v}</span>,
            },
            { title: "Role", dataIndex: "role", width: 130, render: (role: SessionUser["role"]) => <RoleTag role={role} /> },
            {
              title: "Rate", dataIndex: "hourlyRate", width: 100, align: "right", responsive: ["lg"],
              render: (v: number | null) => v == null
                ? <span style={{ color: "var(--q-text-faint)" }}>—</span>
                : <span className="q-num">${v}</span>,
            },
            {
              title: "Status", dataIndex: "active", width: 120,
              render: (active: boolean) => <StatusTag label={active ? "Active" : "Inactive"} tone={active ? "ok" : "neutral"} dot />,
            },
            {
              title: "", width: 140, align: "right",
              render: (_, record) => (
                <span style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                  <Button size="small" onClick={() => openEditModal(record)}>Edit</Button>
                  {record._id !== userData?._id && (
                    <Button size="small" danger onClick={() => handleDelete(record._id)}>Delete</Button>
                  )}
                </span>
              ),
            },
          ]}
        />
      </DisplayComponent>

      <Modal
        title={editingUser ? "Edit user" : "New user"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={handleSubmit}
        okText={editingUser ? "Save" : "Create user"}
        confirmLoading={createUser.isPending || updateUser.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          {!editingUser && <Form.Item name="username" label="Username" rules={[{ required: true }]}><Input /></Form.Item>}
          {!editingUser && <Form.Item name="password" label="Password" rules={[{ required: true, min: 6 }]}><Input.Password /></Form.Item>}
          <Form.Item name="email" label="Email"><Input /></Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select options={ROLES.map((r) => ({ label: ROLE_LABEL[r], value: r }))} disabled={editingUser?._id === userData?._id} />
          </Form.Item>
          {editingUser && (
            <Form.Item name="active" label="Active" valuePropName="checked">
              <Switch disabled={editingUser?._id === userData?._id} />
            </Form.Item>
          )}
          <Form.Item name="assignedClientIds" label="Assigned clients">
            <Select mode="multiple" options={(clients || []).map((c: ClientProps) => ({ label: c.name, value: c._id }))} />
          </Form.Item>
          <Form.Item name="assignedProjectIds" label="Assigned projects">
            <Select mode="multiple" options={(projects || []).map((p: ProjectProps) => ({ label: p.name, value: p._id }))} />
          </Form.Item>
          <Form.Item name="hourlyRate" label="Hourly rate">
            <InputNumber style={{ width: "100%" }} min={0} prefix="$" />
          </Form.Item>
        </Form>
      </Modal>
    </PageShell>
  );
};
